import type { AccessPrivilege } from '@stopal/shared/access-privilege'
import { isQuoteFlow, type QuoteFlow } from '@stopal/shared/order-form'
import { REFERRAL_LINK_ROUTE } from '@stopal/shared/referral'
import {
  createRootRoute,
  createRoute,
  createRouter,
  HeadContent,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { validateInvoicesSearch } from '@/app/app/billing/business/invoices-search'
import AppErrorPage from '@/app/app/error'
import { validateInventorySearch } from '@/app/app/inventory/inventory-search'
import { validateOrdersSearch } from '@/app/app/orders/business/orders-search'
import ConsentErrorPage from '@/app/consent/error'
import DriverErrorPage from '@/app/driver/error'
import RootErrorPage from '@/app/error'
import NotFoundPage from '@/app/not-found'
import OpsErrorPage from '@/app/ops/error'
import ReferrerErrorPage from '@/app/referrer/error'
import {
  type EstimateHandoffSearch,
  validateEstimateHandoffSearch,
} from '@/components/estimator/handoff-search'
import { ImpersonationBanner } from '@/components/shared/impersonation-banner'
import { RequirePrivilege } from '@/components/shared/require-privilege'
import { RECENT_TABS } from '@/components/studio/library/recent-tabs'
import { analyticsUrl } from '@/lib/analytics-url'
import { brands, brandTitle, detectBrand } from '@/lib/brand'
import { pushEvent } from '@/lib/gtm'
import { withPostHog } from '@/lib/posthog'
import { isNative } from '@/lib/runtime'
import { initClickTracking } from '@/lib/track-clicks'
import { getClientQueryClient, trpc } from '@/trpc/client'

const page = (importer: () => Promise<{ default: React.ComponentType }>) =>
  lazyRouteComponent(importer)

const privilegedPage = (
  anyOf: readonly AccessPrivilege[],
  importer: () => Promise<{ default: React.ComponentType }>,
) =>
  page(async () => {
    const { default: Page } = await importer()
    return {
      default: function PrivilegedPage() {
        return (
          <RequirePrivilege anyOf={anyOf}>
            <Page />
          </RequirePrivilege>
        )
      },
    }
  })

// Resolved per navigation, not at module load: the brand is the HOST's (or a
// `?brand=` override's), and a literal here named the consumer product on
// stopalbusiness.com.
const titleHead = (page: string) => () => ({
  meta: [{ title: brandTitle(page) }],
})

const rootRoute = createRootRoute({
  head: () => {
    const brand = detectBrand()
    return {
      meta: [
        { title: brand.name },
        { name: 'description', content: brand.description },
      ],
    }
  },
  component: function RootComponent() {
    return (
      <>
        <HeadContent />
        <ImpersonationBanner />
        <Outlet />
      </>
    )
  },
  errorComponent: ({ error, reset }) => (
    <RootErrorPage error={error} reset={reset} />
  ),
  notFoundComponent: () => <NotFoundPage />,
})

// ── Public / top-level routes ────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (isNative()) throw redirect({ to: '/start', replace: true })
  },
  head: () => {
    const brand = detectBrand()
    return {
      meta: [
        { title: `${brand.name} — ${brand.tagline}` },
        { name: 'description', content: brand.landingDescription },
      ],
    }
  },
  component: page(() => import('@/app/page')),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/login',
  head: titleHead('Sign in'),
  component: page(() => import('@/app/login/page')),
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/register',
  head: titleHead('Get started'),
  component: page(() => import('@/app/register/page')),
})

// Bridges browser-based OAuth into the native Capacitor WebView (see
// apps/web/src/lib/native-auth.ts). Runs in the in-app browser to start/mint,
// and in the WebView to consume the one-time token.
const nativeAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/auth/native',
  head: titleHead('Signing in'),
  component: page(() => import('@/app/auth/native/page')),
})

// Driven by the worker's headless browser to turn a label into a PDF. Renders
// nothing on its own and reads nothing from its URL — see the page's docblock
// for why that is what makes it safe to serve unauthenticated.
const printRenderRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/print/render',
  head: titleHead('Label renderer'),
  component: page(() => import('@/app/print/render/page')),
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/forgot-password',
  head: titleHead('Reset password'),
  component: page(() => import('@/app/forgot-password/page')),
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/reset-password',
  head: titleHead('Set new password'),
  component: page(() => import('@/app/reset-password/page')),
})

// Where the "choose your password" email lands — a login we created for
// somebody. Public: they have no password yet, and the token is the credential.
const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  path: '/set-password',
  head: titleHead('Choose your password'),
  component: page(() => import('@/app/set-password/page')),
})

// Where an invited employee lands from their email. Public — the invitee may
// have no login yet — and the token in the query is the credential.
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      { title: `Join your team — ${brands.business.name}` },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  path: '/join',
  component: page(() => import('@/app/join/page')),
})

// Public: a shared Studio page, opened by someone with no account — the token
// in the query string is the credential, and is stripped out of the address bar
// by the inline script in index.html before analytics can read it.
const readRoute = createRoute({
  getParentRoute: () => rootRoute,
  staticData: { anonymous: true },
  head: () => ({
    meta: [
      { title: brandTitle('Shared document') },
      // A shared link is meant for one recipient. Keeping it out of an index
      // does not make the token secret, but a crawled link is a leaked one.
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  path: '/read',
  component: page(() => import('@/app/read/page')),
})

// Public: the customer answers a driver's swap request from an emailed link,
// with no session — the token in the query string is the credential.
const confirmPalletRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-pallet',
  head: titleHead('Confirm your pallet'),
  component: page(() => import('@/app/confirm-pallet/page')),
})

// Public: the customer accepts the terms from an emailed link after a phone
// order, with no session — the token in the query string is the credential,
// and many of these customers have never set a password.
const confirmTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-terms',
  head: titleHead('Accept our terms'),
  component: page(() => import('@/app/confirm-terms/page')),
})

// Public: the customer collects their own pallets from a place, from a link
// the phone desk emailed, with no session — the token in the query string is
// the credential, and many of these customers have never set a password.
const collectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collect',
  head: titleHead('Collect your pallets'),
  component: page(() => import('@/app/collect/page')),
})

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  head: titleHead('Help & FAQs'),
  component: page(() => import('@/app/help/page')),
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  head: titleHead('Terms & Conditions'),
  component: page(() => import('@/app/terms/page')),
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  head: titleHead('Privacy Policy'),
  component: page(() => import('@/app/privacy/page')),
})

const disclaimerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/disclaimer',
  head: titleHead('Disclaimer'),
  component: page(() => import('@/app/disclaimer/page')),
})

const offlineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline',
  head: () => ({
    meta: [
      { title: brandTitle('Offline') },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  component: page(() => import('@/app/offline/page')),
})

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/start',
  component: page(() => import('@/app/start/page')),
})

const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consent',
  errorComponent: ({ error, reset }) => (
    <ConsentErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/consent/page')),
})

// Public pallet estimator — photos in, a pallet count out, no account. Its own
// route so it is linkable and shareable, and so a phone-camera flow gets the
// whole screen instead of a slot inside a scrolling marketing page.
const estimateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/estimate',
  head: titleHead('How many pallets do you need?'),
  component: page(() => import('@/app/estimate/page')),
})

// A referral QR's link. The QR carries the code so checkout's Scan code can
// read it; a phone camera that opens the link just lands on the site — ruled
// by Marcus 2026-09-22: nothing is remembered, the code counts only when it is
// scanned or typed at checkout.
const referralLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: REFERRAL_LINK_ROUTE,
  beforeLoad: () => {
    throw redirect({ to: '/', replace: true })
  },
})

// `?flow=` preselects a service — from the landing hero on `/checkout`, and on
// `/app` when `/checkout` bounces a signed-in visitor there. One reader for both
// mounts, so the bounce can forward the param without trusting anything new.
// Flow values are checked against the canonical QUOTE_FLOWS taxonomy.
// `?pallets=` / `?kit=` carry an estimate in from /estimate — validated by the
// one shared reader, never trusted, since a search param is attacker-writable.
type OrderEntrySearch = { flow?: QuoteFlow } & EstimateHandoffSearch

const validateOrderEntrySearch = (
  search: Record<string, unknown>,
): OrderEntrySearch => ({
  ...validateEstimateHandoffSearch(search),
  // Present even when refused: a key left out is refilled from the raw URL
  // (see validateEstimateHandoffSearch).
  flow: isQuoteFlow(search.flow) ? search.flow : undefined,
})

// Public guest checkout — the order-flow wizard outside the /app RequireRole
// shell. Anonymous visitors build an order and pay after an email gate.
export const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  validateSearch: validateOrderEntrySearch,
  component: page(() => import('@/app/checkout/page')),
})

// ── /app surface (customer) ──────────────────────────────────────────────
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  errorComponent: ({ error, reset }) => (
    <AppErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/app/layout')),
})

// The customer home — the order wizard. Exported so the page can read its own
// typed search: `/app/estimate` hands its count over through the SAME
// `?pallets=` / `?kit=` params `/checkout` takes, rather than inventing a second mechanism for the
// signed-in mount.
export const appHomeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app',
  validateSearch: validateOrderEntrySearch,
  component: page(() => import('@/app/app/page')),
})

// Exported so the page can read its own typed param, the way `appHomeRoute`
// and `checkoutRoute` are exported to read their typed search. The alternative
// is the `@/compat/navigation` shim the older detail pages use, which casts
// because the compat boundary cannot know the route.
export const venueStayRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/venues/$stayId',
  component: page(() => import('@/app/app/venues/[stayId]/page')),
})

export const appOrdersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/orders',
  validateSearch: validateOrdersSearch,
  component: page(() => import('@/app/app/orders/page')),
})

export const appBillingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/billing',
  validateSearch: validateInvoicesSearch,
  component: page(() => import('@/app/app/billing/page')),
})

export const appInventoryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/inventory',
  validateSearch: validateInventorySearch,
  component: page(() => import('@/app/app/inventory/page')),
})

const appRoutes = [
  appHomeRoute,
  // The signed-in estimator. Its own destination rather than a card above the
  // wizard on `/app`, reached from its own nav tab. It hands its count back to
  // `/app` through the same two params `/estimate` and `/checkout` use.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/estimate',
    component: page(() => import('@/app/app/estimate/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/welcome',
    component: page(() => import('@/app/app/welcome/page')),
  }),
  appBillingRoute,
  // One invoice, opened from the list above. Declared AFTER the static
  // `/app/billing` for the same reason `/app/pallets/scan` precedes
  // `/app/pallets/$id`: a dynamic segment must not swallow a sibling path.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/billing/$invoiceId',
    component: page(() => import('@/app/app/billing/[invoiceId]/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/payment-methods',
    component: page(() => import('@/app/app/payment-methods/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/order',
    component: page(() => import('@/app/app/order/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/order/success',
    component: page(() => import('@/app/app/order/success/page')),
  }),
  appOrdersRoute,
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/orders/$id',
    component: page(() => import('@/app/app/orders/[id]/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/pallets',
    component: page(() => import('@/app/app/pallets/page')),
  }),
  appInventoryRoute,
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/inventory/$assetId',
    component: page(() => import('@/app/app/inventory/[assetId]/page')),
  }),
  // Venues — a dated venue a business occupies for a few days. Its whole
  // subtree is full-bleed (`FULL_BLEED_SUBTREES`), because the stay detail
  // docks a pallet panel beside its table.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/venues',
    loader: () =>
      getClientQueryClient().ensureQueryData(trpc.venue.list.queryOptions()),
    component: page(() => import('@/app/app/venues/page')),
  }),
  venueStayRoute,
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/pallets/action',
    component: page(() => import('@/app/app/pallets/action/page')),
  }),
  // Static segment, so it must not be swallowed by `/app/pallets/$id`.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/pallets/scan',
    component: page(() => import('@/app/app/pallets/scan/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/pallets/$id',
    component: page(() => import('@/app/app/pallets/[id]/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/supplies',
    component: page(() => import('@/app/app/supplies/page')),
  }),
  // Business-only: who can reach the company account. The page itself is
  // gated by the account-scoped procedures it calls — a consumer reaching
  // this URL gets FORBIDDEN from the API, not a rendered empty table.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/team',
    component: page(() => import('@/app/app/team/page')),
  }),
  // Business-only, like /app/team: orders waiting for approval. Gated by the
  // procedures it calls — each asserts the permission its half needs.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/approvals',
    component: page(() => import('@/app/app/approvals/page')),
  }),
  // Same shape as /app/team above: business-only in the nav, and gated by the
  // account-scoped procedures it calls rather than by the route.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/sites',
    component: page(() => import('@/app/app/sites/page')),
  }),
  // One site's own page, reached from its card. Gated the same way: the
  // per-site read resolves through `sitesReadableBy`, so a member restricted
  // away from it gets NOT_FOUND rather than a rendered shell.
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/sites/$siteId',
    component: page(() => import('@/app/app/sites/[siteId]/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account',
    component: page(() => import('@/app/app/account/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/profile',
    component: page(() => import('@/app/app/account/profile/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/addresses',
    component: page(() => import('@/app/app/account/addresses/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/notifications',
    component: page(() => import('@/app/app/account/notifications/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/appearance',
    component: page(() => import('@/app/app/account/appearance/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/connected',
    component: page(() => import('@/app/app/account/connected/page')),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/app/account/delete',
    component: page(() => import('@/app/app/account/delete/page')),
  }),
]

// ── /driver surface ──────────────────────────────────────────────────────
const driverLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'driver-layout',
  errorComponent: ({ error, reset }) => (
    <DriverErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/driver/layout')),
})

const driverRoutes = [
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver',
    component: page(() => import('@/app/driver/page')),
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/stops/$stopId',
    component: page(() => import('@/app/driver/stops/[stopId]/page')),
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/routes/$date',
    component: page(() => import('@/app/driver/routes/[date]/page')),
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/load/$date',
    component: page(() => import('@/app/driver/load/[date]/page')),
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/drop-off',
    component: page(() => import('@/app/driver/drop-off/page')),
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/account',
    component: page(() => import('@/app/driver/account/page')),
  }),
  // The stops list absorbed the dashboard, the deliveries list and the old
  // routes list. Redirect rather than drop the old paths: a driver's installed
  // PWA and any already-delivered assignment notification still point at them.
  // `/driver/routes` with no day is the routes list, which is `/driver`.
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/deliveries',
    beforeLoad: () => {
      throw redirect({ to: '/driver', replace: true })
    },
  }),
  createRoute({
    getParentRoute: () => driverLayoutRoute,
    path: '/driver/routes',
    beforeLoad: () => {
      throw redirect({ to: '/driver', replace: true })
    },
  }),
]

// ── /referrer surface ────────────────────────────────────────────────────
// A referrer's own codes and earnings. Admitted by the portal PRIVILEGE, not by
// a role, so it has no `Surface` key and no `ROLE_SURFACES` entry; the surface
// switcher offers it through `PRIVILEGE_SURFACE_ITEMS`.
const referrerLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'referrer-layout',
  errorComponent: ({ error, reset }) => (
    <ReferrerErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/referrer/layout')),
})

const referrerRoutes = [
  createRoute({
    getParentRoute: () => referrerLayoutRoute,
    path: '/referrer',
    head: titleHead('Referrals'),
    component: page(() => import('@/app/referrer/page')),
  }),
  createRoute({
    getParentRoute: () => referrerLayoutRoute,
    path: '/referrer/agreement',
    head: titleHead('Referral Programme Agreement'),
    component: page(() => import('@/app/referrer/agreement/page')),
  }),
]

// ── /studio surface ──────────────────────────────────────────────────────
// Where pages written for people outside the company live, and where their
// share links are minted and revoked. The pages themselves are authored over
// MCP; this surface reads them and manages who can see them.
const studioLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'studio-layout',
  errorComponent: ({ error, reset }) => (
    <OpsErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/studio/layout')),
})

// The library — Recents, All work, folders, Settings — sits in a shell with a
// sidebar. A page itself opens full width without one, as a file does.
const studioLibraryRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  id: 'studio-library',
  component: page(() => import('@/app/studio/library-layout')),
})

const studioPageRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  id: 'studio-page',
  component: page(() => import('@/app/studio/page-layout')),
})

const studioLibraryRoutes = [
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio',
    head: titleHead('Recents — Studio'),
    validateSearch: (search: Record<string, unknown>) => ({
      tab: RECENT_TABS.find((tab) => tab === search.tab),
    }),
    component: page(() => import('@/app/studio/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/all',
    head: titleHead('All work — Studio'),
    component: page(() => import('@/app/studio/all/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/unfiled',
    head: titleHead('Unfiled — Studio'),
    component: page(() => import('@/app/studio/unfiled/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/folders',
    head: titleHead('All folders — Studio'),
    component: page(() => import('@/app/studio/folders/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/folder/$folderId',
    head: titleHead('Folder — Studio'),
    component: page(() => import('@/app/studio/folder/[folderId]/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/media',
    head: titleHead('Media — Studio'),
    component: page(() => import('@/app/studio/media/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/settings',
    head: titleHead('Settings — Studio'),
    component: page(() => import('@/app/studio/settings/page')),
  }),
  createRoute({
    getParentRoute: () => studioLibraryRoute,
    path: '/studio/pronunciation',
    head: titleHead('Pronunciation — Studio'),
    component: page(() => import('@/app/studio/pronunciation/page')),
  }),
]

const studioPageRoutes = [
  createRoute({
    getParentRoute: () => studioPageRoute,
    path: '/studio/$id',
    head: titleHead('Page — Studio'),
    component: page(() => import('@/app/studio/[id]/page')),
  }),
  createRoute({
    getParentRoute: () => studioPageRoute,
    path: '/studio/$id/sharing',
    head: titleHead('Sharing — Studio'),
    component: page(() => import('@/app/studio/[id]/sharing/page')),
  }),
]

// ── /logistics surface ───────────────────────────────────────────────────
// Logistics is its own surface on a simple Nexus shell (no sidebar): the user
// lands on the launcher and each tool opens full-page with a back to it. The
// same tools are also mirrored in the /ops App Launcher (the integrated half).
const logisticsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'logistics-layout',
  errorComponent: ({ error, reset }) => (
    <OpsErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/logistics/layout')),
})

const logisticsRoutes = [
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics',
    component: page(() => import('@/app/logistics/page')),
  }),
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics/inventory',
    component: privilegedPage(
      ['access_warehouses'],
      () => import('@/app/ops/logistics/inventory'),
    ),
  }),
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics/assign',
    component: privilegedPage(
      ['access_warehouses'],
      () => import('@/app/ops/logistics/assign'),
    ),
  }),
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics/receiving',
    component: privilegedPage(
      ['access_warehouses'],
      () => import('@/app/ops/logistics/receiving'),
    ),
  }),
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics/dashboard',
    component: privilegedPage(['access_dashboard'], () =>
      import('@/app/ops/dashboard/dashboard').then((m) => ({
        default: m.Dashboard,
      })),
    ),
  }),
  createRoute({
    getParentRoute: () => logisticsLayoutRoute,
    path: '/logistics/dispatch',
    component: privilegedPage(['access_dispatch'], () =>
      import('@/app/ops/dispatch/dispatch-page').then((m) => ({
        default: m.DispatchPage,
      })),
    ),
  }),
]

// ── /warehouse → /logistics redirects ────────────────────────────────────
// The old standalone /warehouse surface is now the /logistics surface.
const warehouseRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/warehouse',
  beforeLoad: () => {
    throw redirect({ to: '/logistics/inventory', replace: true })
  },
})

const warehouseAssignRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/warehouse/assign',
  beforeLoad: () => {
    throw redirect({ to: '/logistics/assign', replace: true })
  },
})

// ── /ops surface ─────────────────────────────────────────────────────────
// The layout mounts the AppShell, which reads the URL and renders the right
// tab. The child routes render nothing; the splat keeps every /ops/* URL valid.
const opsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'ops-layout',
  errorComponent: ({ error, reset }) => (
    <OpsErrorPage error={error} reset={reset} />
  ),
  component: page(() => import('@/app/ops/layout')),
})

const opsIndexRoute = createRoute({
  getParentRoute: () => opsLayoutRoute,
  path: '/ops',
  component: () => null,
})

const opsSplatRoute = createRoute({
  getParentRoute: () => opsLayoutRoute,
  path: '/ops/$',
  component: () => null,
})

// Legacy path from before the Pricing→Products rename.
const opsPricingRedirectRoute = createRoute({
  getParentRoute: () => opsLayoutRoute,
  path: '/ops/pricing',
  beforeLoad: () => {
    throw redirect({
      to: '/ops/$',
      params: { _splat: 'products' },
      replace: true,
    })
  },
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  joinRoute,
  nativeAuthRoute,
  forgotPasswordRoute,
  printRenderRoute,
  resetPasswordRoute,
  setPasswordRoute,
  readRoute,
  confirmPalletRoute,
  confirmTermsRoute,
  collectRoute,
  helpRoute,
  termsRoute,
  privacyRoute,
  disclaimerRoute,
  offlineRoute,
  startRoute,
  consentRoute,
  estimateRoute,
  referralLinkRoute,
  checkoutRoute,
  appLayoutRoute.addChildren(appRoutes),
  driverLayoutRoute.addChildren(driverRoutes),
  logisticsLayoutRoute.addChildren(logisticsRoutes),
  studioLayoutRoute.addChildren([
    studioLibraryRoute.addChildren(studioLibraryRoutes),
    studioPageRoute.addChildren(studioPageRoutes),
  ]),
  referrerLayoutRoute.addChildren(referrerRoutes),
  warehouseRedirectRoute,
  warehouseAssignRedirectRoute,
  opsLayoutRoute.addChildren([
    opsIndexRoute,
    opsPricingRedirectRoute,
    opsSplatRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

router.subscribe('onLoad', ({ toLocation }) => {
  // `toLocation.href` is pathname + search + hash, so sending it raw would hand
  // every query param to PostHog and the GTM dataLayer — including the
  // invitation bearer token on /join and the copy of it that /login carries in
  // `?next=`. `analyticsUrl` keeps the path and an allowlist of funnel params.
  const url = analyticsUrl(toLocation.href)
  withPostHog((ph) => ph.capture('$pageview', { $current_url: url }))
  pushEvent('page_view', { page_path: url })
})

// Delegated click tracking: any `data-el`-tagged element emits a `ui_click`
// dataLayer event for GTM. Registered once; no-ops outside the browser.
initClickTracking()

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
